import type { ScheduleRole, ScheduleRow } from './types';

export const SCHEDULE_ROLE_LABELS: Readonly<Record<ScheduleRole, string>> = {
    owner: 'Owner',
    subscriber: 'Subscriber',
    other: 'Other zone',
};

export interface ScheduleRoleContext {
    /** The `location_zone_id` of the list the schedule is on. */
    ownerZoneId: number | null | undefined;
    /**
     * The `master_location_zone_id` of the zone that wrote the schedule, from a
     * zone lookup. `undefined` while that lookup has not answered; `null` when
     * the zone has no master.
     */
    zoneMasterId: number | null | undefined;
}

/**
 * Who wrote this schedule, relative to the list's owner zone.
 *
 * - 'owner': the schedule's zone is the list's own zone.
 * - 'subscriber': the schedule's zone is a subscriber of the list's owner.
 * - 'other': any other zone — no master, a different master, or a sibling
 *   subscriber. Production has 128 of these; they must never read "Subscriber".
 * - null: the schedule is on another zone and its master is not known yet, so
 *   the caller should render a neutral state.
 */
export function scheduleRole(
    row: ScheduleRow,
    { ownerZoneId, zoneMasterId }: ScheduleRoleContext,
): ScheduleRole | null {
    const zoneId = row.location_zone_id ?? null;
    if (ownerZoneId !== null && ownerZoneId !== undefined && zoneId !== null && Number(zoneId) === Number(ownerZoneId)) {
        return 'owner';
    }
    if (zoneMasterId === undefined) {
        return null;
    }
    if (
        zoneMasterId !== null &&
        ownerZoneId !== null &&
        ownerZoneId !== undefined &&
        Number(zoneMasterId) === Number(ownerZoneId)
    ) {
        return 'subscriber';
    }
    return 'other';
}
